import React, { ReactElement, useCallback, useEffect } from 'react'
import { CardNumberElement, CardExpiryElement, CardCvcElement } from '@stripe/react-stripe-js'
import { Box, Flex, Label, Text, ThemeUIStyleObject } from 'theme-ui'
import { Input } from 'ui'
import InfoIcon from 'images/dashboard/info-icon.svg'
import IconWithTippyPopup from 'ui/IconWithTippyPopup'
import { inputLabelNewStyles } from 'ui/Inputs/Input.base'
import { newInputSharedStyles } from 'ui/Theme/customComponents/forms'
import useGetColorMode from 'utils/useGetColorMode'

interface CreditCardInputsProps {
  fullName: string
  setFullName: (string) => void
}

const inputStyles = {
  ...(newInputSharedStyles as ThemeUIStyleObject),
  alignSelf: 'unset',
  transform: 'unset',
  p: 0,
  height: '3.8rem',
  fontSize: '1.4rem',
  '& > .StripeElement': {
    borderRadius: '1.2rem',
    px: '1.6rem',
    py: '1rem',
  },
  '& > .StripeElement--focus': {
    px: '1.6rem',
    py: '1rem',
    outline: 'none',
    border: '1px solid',
    borderColor: 'cyan800',
    height: '3.8rem',
    borderRadius: '0.8rem',
  },
  '& > .StripeElement--webkit-autofill': {
    backgroundColor: '#050C1D !important', // it doesn't work with colors from palette
  },
}

export default function CreditCardInputs({
  fullName,
  setFullName,
}: CreditCardInputsProps): ReactElement {
  /**
   Note: The user agent style sheets of many browsers use !important in their :-webkit-autofill style declarations, making them non-overrideable by webpages without resorting to JavaScript hacks. For example Chrome has the following in its internal stylesheet:
      background-color: rgb(232, 240, 254) !important;
      background-image: none !important;
      color: -internal-light-dark(black, white) !important;
   This means that you cannot set the background-color, background-image, or color in your own rules.
   The solution below helps clear up these objections.
	 */
  const CARD_ELEMENT_OPTIONS = {
    style: {
      base: {
        backgroundColor: 'white10',
        color: 'aliceBlue',
        fontFamily: 'Plus Jakarta Sans Variable, sans-serif',
        fontSmoothing: 'antialiased',
        padding: '0.8px 1.2px',
        fontSize: '14px',
        lineHeight: '16px',
        webkitTextFillColor: 'pomegranate',
        outline: 'none',
        '::placeholder': {
          color: 'aliceBlue70',
        },
        ':-webkit-autofill': {
          color: '#E8EFFF', // it doesn't work with colors from palette
        },
      },
      invalid: {
        color: '#fa755a',
        iconColor: '#fa755a',
      },
    },
  }

  const updateAutofillSelection = useCallback(() => {
    CARD_ELEMENT_OPTIONS.style.base = {
      ...CARD_ELEMENT_OPTIONS.style.base,
    }
  }, [CARD_ELEMENT_OPTIONS.style])

  useEffect(() => {
    updateAutofillSelection()
  }, [updateAutofillSelection])

  const { isLightMode } = useGetColorMode()

  return (
    <Flex sx={{ textAlign: 'left', flexDirection: 'column', gap: '2.4rem' }}>
      <Input
        data-testid="full-name-input"
        value={fullName}
        onChange={(e): void => {
          setFullName(e.target.value)
        }}
        label="Full Name"
        labelColor="aliceBlue60"
        sx={{
          ...inputStyles,
          p: '1.6rem',
        }}
        hasText={false}
      />

      <Flex sx={{ flexDirection: 'column', gap: '0.8rem' }}>
        <Label className="card-input-label" style={{ ...inputLabelNewStyles }}>
          Credit Card Number
        </Label>
        <Box
          className="card-input"
          data-testid="number"
          sx={{
            ...inputStyles,
            alignSelf: 'unset',
          }}
        >
          <CardNumberElement options={CARD_ELEMENT_OPTIONS} />
        </Box>
      </Flex>

      <Flex
        className="stripe-row"
        style={{
          gap: '2.4rem',
        }}
      >
        <Flex
          sx={{
            flexDirection: 'column',
            flex: 1,
            gap: '0.8rem',
          }}
        >
          <Label className="expiry-date-input-label" style={{ ...inputLabelNewStyles }}>
            Expiry Date
          </Label>
          <Box
            className="card-input"
            data-testid="expiry"
            sx={{ ...inputStyles, alignSelf: 'unset' }}
          >
            <CardExpiryElement options={CARD_ELEMENT_OPTIONS} />
          </Box>
        </Flex>
        <Flex sx={{ flexDirection: 'column', flex: [1, 'unset', 1], gap: '0.8rem' }}>
          <Flex
            sx={{
              width: '100%',
              alignItems: 'center',
              justifyContent: 'flex-start',
            }}
          >
            <Label
              className="security-code-label"
              sx={{
                ...inputLabelNewStyles,
                width: 'auto',
                opacity: 1,
              }}
            >
              <Text
                sx={{
                  width: 'auto',
                  mr: '0.8rem',
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  color: 'aliceBlue60',
                }}
              >
                Security Code
              </Text>
            </Label>
            <IconWithTippyPopup
              theme={isLightMode ? 'light-org-tooltip' : 'org-tooltip'}
              content={
                <Text
                  variant="size12Weight400"
                  sx={{
                    display: 'inline-block',
                    width: '19rem',
                  }}
                >
                  Your security code is a 3 or 4 digit number that is found on the back of your
                  card.
                </Text>
              }
              ariaLabel="Your security code is a 3 or 4 digit number that is found on the back of your
                  card."
              svg={InfoIcon}
              descriptiveText="Security code information"
              sx={{
                width: '1.6rem',
                height: '1.6rem',
                cursor: 'pointer',
                color: 'aliceBlue',
                opacity: '0.5',
                ':hover': {
                  opacity: 1,
                },
                '@media (hover: none)': {
                  '&:hover': { opacity: 1 },
                },
              }}
            />
          </Flex>
          <Box className="card-input" data-testid="cvc" sx={{ ...inputStyles, alignSelf: 'unset' }}>
            <CardCvcElement options={CARD_ELEMENT_OPTIONS} />
          </Box>
        </Flex>
      </Flex>
    </Flex>
  )
}
